<template>
  <div class="sci-view-container">
    <!-- Header -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button @click.prevent="home" class="sci-btn-back" title="Torna al Gestionale">
          <i class="fas fa-arrow-left"></i>
          <span>Gestionale</span>
        </button>
        <div class="sci-title-group">
          <div class="sci-badge-tag">
            <i class="fas fa-file-invoice"></i> Cassa & Fiscale
          </div>
          <h1 class="sci-view-title">Gestione Fatture Passive & Riconciliazione</h1>
          <p class="sci-view-subtitle">Caricamento automatico fatture fornitori (PDF/Immagine), matching articoli e carico magazzino</p>
        </div>
      </div>
      <div class="sci-header-actions">
        <button class="sci-btn sci-btn-secondary" @click="fetchInvoices" title="Ricarica elenco fatture">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': pollingInterval }"></i>
          <span>Aggiorna</span>
        </button>
      </div>
    </header>

    <!-- Bento KPI Grid -->
    <section class="sci-kpi-grid">
      <div class="sci-kpi-card primary">
        <div class="kpi-icon-wrap blue">
          <i class="fas fa-file-invoice"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">Fatture Totali</span>
          <span class="kpi-value">{{ invoices.length }}</span>
          <span class="kpi-sub">Documenti ricevuti in archivio</span>
        </div>
      </div>

      <div class="sci-kpi-card">
        <div class="kpi-icon-wrap amber">
          <i class="fas fa-tasks"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">Da Riconciliare</span>
          <span class="kpi-value">{{ pendingReconcileCount }}</span>
          <span class="kpi-sub">Richiedono verifica articoli</span>
        </div>
      </div>

      <div class="sci-kpi-card">
        <div class="kpi-icon-wrap green">
          <i class="fas fa-check-double"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">Completate</span>
          <span class="kpi-value">{{ completedCount }}</span>
          <span class="kpi-sub">Articoli caricati in magazzino</span>
        </div>
      </div>

      <div class="sci-kpi-card">
        <div class="kpi-icon-wrap purple">
          <i class="fas fa-robot"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">In Elaborazione OCR</span>
          <span class="kpi-value">{{ processingCount }}</span>
          <span class="kpi-sub">Analisi automatica in corso</span>
        </div>
      </div>
    </section>

    <!-- Upload & Controls Row -->
    <div class="sci-controls-grid">
      <!-- Upload Card -->
      <section class="sci-card upload-card">
        <div class="upload-inner">
          <div class="upload-icon-box">
            <i class="fas fa-cloud-upload-alt"></i>
          </div>
          <div class="upload-text-box">
            <h4>Carica Nuova Fattura Fornitore</h4>
            <p>Trascina un file PDF o immagine (.png, .jpg) oppure selezionalo dal dispositivo</p>
          </div>
          <div class="upload-actions">
            <label class="sci-file-trigger">
              <input type="file" @change="handleFileUpload" accept=".pdf,image/*" ref="fileInput" />
              <i class="fas fa-paperclip"></i>
              <span>{{ selectedFile ? selectedFile.name : 'Scegli File...' }}</span>
            </label>
            <button
              class="sci-btn sci-btn-primary"
              @click="uploadInvoice"
              :disabled="!selectedFile || uploading"
            >
              <i class="fas fa-spinner fa-spin" v-if="uploading"></i>
              <i class="fas fa-upload" v-else></i>
              <span>{{ uploading ? 'Caricamento...' : 'Carica Fattura' }}</span>
            </button>
          </div>
        </div>
      </section>

      <!-- Filters Card -->
      <section class="sci-card filters-card">
        <div class="filters-inner">
          <div class="date-filters">
            <div class="sci-input-field">
              <label><i class="far fa-calendar-alt"></i> Da:</label>
              <input type="date" v-model="startDate" class="sci-input" />
            </div>
            <div class="sci-input-field">
              <label><i class="far fa-calendar-alt"></i> A:</label>
              <input type="date" v-model="endDate" class="sci-input" />
            </div>
          </div>
          <div class="sci-search-field">
            <i class="fas fa-search sci-search-icon"></i>
            <input
              type="text"
              v-model="productSearchQuery"
              placeholder="Cerca per prodotto (nome o codice)..."
              class="sci-input search-input"
            />
            <button v-if="productSearchQuery" class="sci-clear-btn" @click="productSearchQuery = ''">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
      </section>
    </div>

    <!-- Invoices List Table Card -->
    <section class="sci-card sci-table-card">
      <div class="table-responsive" v-if="filteredInvoices.length">
        <table class="sci-table">
          <thead>
            <tr>
              <th>Data Fattura</th>
              <th>Partner / Fornitore</th>
              <th>Stato / Articoli Rilevati</th>
              <th class="text-end">Azioni</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="invoice in filteredInvoices" :key="invoice._id">
              <td>
                <div class="date-cell">
                  <i class="far fa-calendar-check"></i>
                  <span>{{ formatDate(invoice.created_at) }}</span>
                </div>
              </td>
              <td>
                <div class="partner-cell">
                  <span class="partner-name">{{ invoice.partner_id ? invoice.partner_id.nome : 'Fornitore Non Identificato' }}</span>
                  <span v-if="invoice.partner_id && invoice.partner_id.partita_iva" class="partner-vat">
                    P.IVA: {{ invoice.partner_id.partita_iva }}
                  </span>
                </div>
              </td>
              <td>
                <span v-if="invoice.stato === 'PENDING'" class="sci-pill-warning">
                  <i class="fas fa-spinner fa-spin"></i> Elaborazione OCR...
                </span>
                <span v-else-if="invoice.stato === 'PROCESSED'" class="sci-pill-info">
                  <i class="fas fa-exclamation-circle"></i> Da Riconciliare ({{ invoice.prodotti ? invoice.prodotti.length : 0 }} art.)
                </span>
                <span v-else-if="invoice.stato === 'COMPLETED'" class="sci-pill-success">
                  <i class="fas fa-check-circle"></i> Carico Completato ({{ invoice.prodotti ? invoice.prodotti.length : 0 }} art.)
                </span>
                <span v-else class="sci-pill-neutral">
                  {{ invoice.prodotti ? invoice.prodotti.length : 0 }} articoli
                </span>
              </td>
              <td class="text-end">
                <div class="table-actions">
                  <button
                    class="sci-btn-action"
                    :class="invoice.stato === 'PROCESSED' ? 'accent' : 'secondary'"
                    @click="viewInvoice(invoice)"
                    :disabled="invoice.stato === 'PENDING'"
                    :title="invoice.stato === 'PROCESSED' ? 'Riconcilia e allinea al magazzino' : 'Visualizza dettagli'"
                  >
                    <i :class="invoice.stato === 'PROCESSED' ? 'fas fa-link' : 'far fa-eye'"></i>
                    <span>{{ invoice.stato === 'PROCESSED' ? 'Riconcilia' : 'Dettagli' }}</span>
                  </button>
                  <button
                    class="sci-btn-action danger"
                    @click="deleteInvoice(invoice._id)"
                    title="Elimina fattura e prodotti associati"
                  >
                    <i class="fas fa-trash-alt"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div v-else class="sci-state-feedback">
        <div class="empty-icon-wrap">
          <i class="fas fa-file-invoice"></i>
        </div>
        <p class="state-title">Nessuna fattura trovata</p>
        <p class="state-subtitle">Non ci sono fatture registrate o corrispondenti ai filtri di ricerca impostati.</p>
      </div>
    </section>

    <!-- Modal Details & Reconciliation -->
    <div v-if="selectedInvoice" class="sci-modal-overlay" @click.self="closeDetails">
      <div class="sci-modal-dialog">
        <!-- Modal Header -->
        <div class="sci-modal-header">
          <div class="modal-title-wrap">
            <span class="sci-badge-tag"><i class="fas fa-file-invoice"></i> Dettaglio Documento</span>
            <h3>Fattura Fornitore - {{ formatDate(selectedInvoice.created_at) }}</h3>
          </div>
          <button @click="closeDetails" class="sci-modal-close" title="Chiudi finestra">&times;</button>
        </div>

        <!-- Modal Body: 2 Columns Split -->
        <div class="sci-modal-body">
          <!-- Col 1: Partner & Document Viewer -->
          <div class="modal-col-left">
            <!-- Partner Data -->
            <div class="sci-card-inner">
              <div class="inner-header">
                <h4><i class="fas fa-building"></i> Dati Fornitore (Partner)</h4>
              </div>
              <div class="inner-form">
                <div class="form-row">
                  <div class="form-group flex-2">
                    <label>Ragione Sociale / Nome:</label>
                    <input type="text" v-model="selectedInvoice.partner_id.nome" class="sci-input" placeholder="Es. Rossi Carni S.r.l." />
                  </div>
                  <div class="form-group flex-1">
                    <label>Partita IVA:</label>
                    <input type="text" v-model="selectedInvoice.partner_id.partita_iva" class="sci-input" placeholder="Es. IT01234567890" />
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-group flex-1">
                    <label>Email Fornitore:</label>
                    <input type="email" v-model="selectedInvoice.partner_id.email" class="sci-input" placeholder="info@fornitore.it" />
                  </div>
                  <div class="form-group flex-1">
                    <label>Telefono:</label>
                    <input type="text" v-model="selectedInvoice.partner_id.telefono" class="sci-input" placeholder="+39 06 1234567" />
                  </div>
                </div>

                <button @click="updatePartnerInfo" class="sci-btn sci-btn-secondary sci-btn-sm mt-2">
                  <i class="fas fa-save"></i>
                  <span>{{ selectedInvoice.partner_id._id ? 'Aggiorna Anagrafica Fornitore' : 'Salva Nuovo Fornitore' }}</span>
                </button>
              </div>
            </div>

            <!-- File Viewer -->
            <div class="sci-card-inner mt-3">
              <div class="inner-header d-flex justify-content-between align-items-center">
                <h4><i class="far fa-file-alt"></i> Documento Originale</h4>
                <button
                  v-if="selectedInvoice.file_path"
                  @click="openExternal(selectedInvoice.file_path)"
                  class="sci-btn-action secondary"
                  title="Apri in nuova scheda"
                >
                  <i class="fas fa-external-link-alt"></i> Apri Esterno
                </button>
              </div>
              <div class="sci-file-viewer-box">
                <div v-if="!selectedInvoice.file_path" class="sci-empty-doc">
                  <i class="fas fa-file-excel"></i>
                  <p>Nessun allegato digitale collegato a questa fattura.</p>
                </div>
                <img
                  :src="getInvoiceUrl(selectedInvoice.file_path)"
                  alt="Fattura"
                  class="img-preview"
                  v-else-if="isImage(selectedInvoice.file_path)"
                />
                <iframe
                  :src="getInvoiceUrl(selectedInvoice.file_path)"
                  class="pdf-preview"
                  v-else
                ></iframe>
              </div>
            </div>
          </div>

          <!-- Col 2: Products Reconciliation Table -->
          <div class="modal-col-right">
            <div class="sci-card-inner h-100">
              <div class="inner-header d-flex justify-content-between align-items-center">
                <div>
                  <h4><i class="fas fa-boxes"></i> Articoli Rilevati & Mappatura Magazzino</h4>
                  <p class="text-muted-sm">Associa ogni articolo a un prodotto di magazzino esistente o creane uno nuovo</p>
                </div>
                <button
                  v-if="selectedInvoice.stato === 'PROCESSED'"
                  @click="confirmReconciliation"
                  class="sci-btn sci-btn-primary"
                  :disabled="!isReadyToConfirm"
                >
                  <i class="fas fa-check-double"></i>
                  <span>Conferma Carico Massivo</span>
                </button>
              </div>

              <div class="table-responsive modal-table-scroll">
                <table class="sci-table compact">
                  <thead>
                    <tr>
                      <th style="width: 15%">Codice</th>
                      <th style="width: 30%">Descrizione Articolo</th>
                      <th style="width: 12%">Q.tà</th>
                      <th style="width: 15%">Prezzo (€)</th>
                      <th style="width: 28%">Associa a Magazzino</th>
                      <th v-if="selectedInvoice.stato === 'COMPLETED'">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(prod, index) in selectedInvoice.prodotti" :key="index">
                      <td>
                        <input
                          v-if="prod.matchedCode === 'NEW'"
                          type="text"
                          v-model="prod.code"
                          class="sci-input-table"
                          placeholder="Nuovo codice"
                        />
                        <span v-else class="code-badge-table">{{ prod.code }}</span>
                      </td>
                      <td>
                        <input type="text" v-model="prod.nome" class="sci-input-table" />
                      </td>
                      <td>
                        <input type="number" v-model="prod.quantita" class="sci-input-table text-center" />
                      </td>
                      <td>
                        <input type="number" step="0.01" v-model="prod.prezzo" class="sci-input-table text-end" />
                      </td>
                      <td>
                        <select v-model="prod.matchedCode" class="sci-select-table">
                          <option :value="null">-- Non associare --</option>
                          <option value="NEW">➕ Crea Nuovo Prodotto</option>
                          <option v-for="p in tuttiProdottiMappati" :key="p.code" :value="p.code">
                            {{ p.descrizio }} ({{ p.code }})
                          </option>
                        </select>
                      </td>
                      <td v-if="selectedInvoice.stato === 'COMPLETED'">
                        <button @click="updateProduct(prod)" class="sci-btn-action success" title="Aggiorna magazzino">
                          <i class="fas fa-save"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { store } from '../store/store.js'

export default {
  name: 'InvoiceManager',
  data() {
    return {
      token: '',
      store,
      invoices: [],
      tuttiProdottiMappati: [],
      selectedFile: null,
      uploading: false,
      selectedInvoice: null,
      pollingInterval: null,
      startDate: '',
      endDate: '',
      productSearchQuery: ''
    }
  },
  watch: {
    invoices: {
      handler(newVal) {
        const hasPending = newVal.some(i => i.stato === 'PENDING' || i.stato === 'PROCESSED');
        if (hasPending && !this.pollingInterval) {
          this.startPolling();
        } else if (!hasPending && this.pollingInterval) {
          this.stopPolling();
        }
      },
      deep: true
    }
  },
  computed: {
    pendingReconcileCount() {
      return this.invoices.filter(i => i.stato === 'PROCESSED').length;
    },
    completedCount() {
      return this.invoices.filter(i => i.stato === 'COMPLETED').length;
    },
    processingCount() {
      return this.invoices.filter(i => i.stato === 'PENDING').length;
    },
    filteredInvoices() {
      return this.invoices.filter(invoice => {
        if (invoice.stato === 'PENDING') return false;

        const date = new Date(invoice.created_at);
        const start = this.startDate ? new Date(this.startDate) : null;
        const end = this.endDate ? new Date(this.endDate) : null;

        if (start && date < start) return false;
        if (end) {
          end.setHours(23, 59, 59, 999);
          if (date > end) return false;
        }

        if (this.productSearchQuery) {
          const query = this.productSearchQuery.toLowerCase();
          const hasProduct = invoice.prodotti && invoice.prodotti.some(p => {
            const nome = (p.nome || '').toLowerCase();
            const code = (p.code || '').toLowerCase();
            return nome.includes(query) || code.includes(query);
          });
          if (!hasProduct) return false;
        }

        return true;
      });
    },
    isReadyToConfirm() {
      return this.selectedInvoice && 
             this.selectedInvoice.prodotti && 
             this.selectedInvoice.prodotti.some(p => p.matchedCode);
    }
  },
  methods: {
    home() {
      this.$router.push({ name: "Gestionale" });
    },
    getToken() {
      try {
        if (!localStorage.token) return '';
        const parsed = JSON.parse(localStorage.token);
        return typeof parsed === 'string' ? parsed : (parsed.token || localStorage.token);
      } catch (e) {
        return localStorage.token || '';
      }
    },
    handleFileUpload(event) {
      this.selectedFile = event.target.files[0];
    },
    async fetchMappa() {
      const token = this.getToken();
      try {
        const response = await this.axios.post(this.store.path + 'displayProduct', {
          headers: { authorization: `Bearer ${token}` }
        }, {
          headers: { authorization: `Bearer ${token}` }
        });
        this.tuttiProdottiMappati = response.data?.messaggio || [];
      } catch (error) {
        console.error('Fetch mappa error:', error);
      }
    },
    async uploadInvoice() {
      if (!this.selectedFile) return;

      this.uploading = true;
      const formData = new FormData();
      formData.append('file', this.selectedFile);
      const token = this.getToken();

      try {
        await this.axios.post(this.store.path + 'fattura/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            authorization: `Bearer ${token}`
          }
        });
        alert('Fattura caricata con successo! In elaborazione automatica...');
        this.selectedFile = null;
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = '';
        }
        this.fetchInvoices();
      } catch (error) {
        console.error('Upload error:', error);
        alert('Errore durante il caricamento della fattura.');
      } finally {
        this.uploading = false;
      }
    },
    async fetchInvoices() {
      const token = this.getToken();
      try {
        const response = await this.axios.get(this.store.path + 'fattura', {
          headers: {
            authorization: `Bearer ${token}`
          }
        });
        this.invoices = response.data || [];
      } catch (error) {
        console.error('Fetch error:', error);
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const d = new Date(dateString);
      return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    viewInvoice(invoice) {
      const copy = JSON.parse(JSON.stringify(invoice));
      if (!copy.partner_id) {
        copy.partner_id = {
          _id: null,
          nome: '',
          partita_iva: '',
          email: '',
          telefono: ''
        };
      }

      if (copy.prodotti) {
        copy.prodotti = copy.prodotti.map(p => {
          const match = this.tuttiProdottiMappati.find(mp => mp.code === p.code);
          return {
            ...p,
            matchedCode: p.matchedCode || (match ? match.code : 'NEW')
          };
        });
      }

      this.selectedInvoice = copy;
    },
    async confirmReconciliation() {
      if (!this.selectedInvoice) return;

      const payload = {
        fatturaId: this.selectedInvoice._id,
        products: this.selectedInvoice.prodotti
          .filter(p => p.matchedCode)
          .map(p => ({
            code: p.matchedCode === 'NEW' ? p.code : p.matchedCode,
            isNew: p.matchedCode === 'NEW',
            quantita: p.quantita,
            prezzo: p.prezzo,
            nome: p.nome
          }))
      };

      const token = this.getToken();
      try {
        await this.axios.post(this.store.path + 'fattura/confirm-load', payload, {
          headers: { authorization: `Bearer ${token}` }
        });
        alert('Prodotti caricati e allineati con il magazzino con successo!');
        this.selectedInvoice = null;
        this.fetchInvoices();
      } catch (error) {
        console.error('Confirm error:', error);
        alert('Errore durante il caricamento riconciliato.');
      }
    },
    closeDetails() {
      this.selectedInvoice = null;
    },
    getInvoiceUrl(filePath) {
      if (!filePath) return '';
      const filename = filePath.split('/').pop();
      let baseUrl = this.store.path.replace('/login/', '/').replace('/login', '/');
      if (!baseUrl.endsWith('/')) baseUrl += '/';
      return `${baseUrl}uploads/${filename}`;
    },
    isImage(filePath) {
      if (!filePath) return false;
      const ext = filePath.split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'jfif', 'pjpeg', 'pjp'].includes(ext);
    },
    isPdf(filePath) {
      if (!filePath) return false;
      const ext = filePath.split('.').pop().toLowerCase();
      return ext === 'pdf';
    },
    openExternal(filePath) {
      window.open(this.getInvoiceUrl(filePath), '_blank');
    },
    async updateProduct(prod) {
      const token = this.getToken();
      try {
        await this.axios.post(this.store.path + 'fattura/update-product', {
          fatturaId: this.selectedInvoice._id,
          code: prod.code,
          newQuantity: prod.quantita,
          newPrice: prod.prezzo,
          newName: prod.nome
        }, {
          headers: {
            authorization: `Bearer ${token}`
          }
        });
        alert('Prodotto aggiornato con successo e allineato col magazzino!');
        this.fetchInvoices();
      } catch (error) {
        console.error('Update error:', error);
        alert('Errore durante l\'aggiornamento del prodotto.');
      }
    },
    async deleteInvoice(id) {
      if (!confirm('Sei sicuro di voler eliminare questa fattura e tutti i prodotti associati?')) return;
      const token = this.getToken();
      try {
        await this.axios.delete(this.store.path + 'fattura/' + id, {
          headers: { authorization: `Bearer ${token}` }
        });
        alert('Fattura eliminata con successo.');
        this.fetchInvoices();
      } catch (error) {
        console.error('Delete error:', error);
        alert('Errore durante l\'eliminazione della fattura.');
      }
    },
    async updatePartnerInfo() {
      if (!this.selectedInvoice) return;
      const token = this.getToken();
      try {
        const partnerData = this.selectedInvoice.partner_id || {};
        const response = await this.axios.post(this.store.path + 'fattura/update-partner', {
          partnerId: partnerData._id || null,
          fatturaId: this.selectedInvoice._id,
          nome: partnerData.nome,
          partita_iva: partnerData.partita_iva,
          email: partnerData.email,
          telefono: partnerData.telefono
        }, {
          headers: { authorization: `Bearer ${token}` }
        });
        alert('Dati fornitore aggiornati con successo!');
        if (response.data && response.data.partner) {
          this.selectedInvoice.partner_id = response.data.partner;
        }
        this.fetchInvoices();
      } catch (error) {
        console.error('Update partner error:', error);
        alert('Errore durante l\'aggiornamento del fornitore.');
      }
    },
    startPolling() {
      if (this.pollingInterval) return;
      this.pollingInterval = setInterval(() => {
        this.fetchInvoices();
      }, 5000);
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    }
  },
  mounted() {
    this.token = this.getToken();
    this.fetchInvoices();
    this.fetchMappa();
  },
  beforeUnmount() {
    this.stopPolling();
  }
}
</script>

<style scoped>
.sci-view-container {
  min-height: 100vh;
  background-color: #f1f5f9;
  padding: 24px 32px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #0f172a;
  box-sizing: border-box;
}

/* Header */
.sci-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  color: #334155;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.sci-btn-back:hover {
  background: #f8fafc;
  border-color: #94a3b8;
  color: #0f172a;
  transform: translateX(-2px);
}

.sci-badge-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #0284c7;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.sci-view-title {
  font-size: 1.65rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  line-height: 1.2;
}

.sci-view-subtitle {
  font-size: 0.9rem;
  color: #64748b;
  margin: 4px 0 0 0;
}

/* Buttons */
.sci-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.sci-btn-primary {
  background: #0284c7;
  color: #ffffff;
}

.sci-btn-primary:hover:not(:disabled) {
  background: #0369a1;
}

.sci-btn-secondary {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  color: #334155;
}

.sci-btn-secondary:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #94a3b8;
}

.sci-btn-sm {
  min-height: 36px;
  padding: 0 12px;
  font-size: 0.85rem;
}

.sci-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Bento KPI Cards */
.sci-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.sci-kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.sci-kpi-card.primary {
  border-left: 4px solid #0284c7;
}

.kpi-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  flex-shrink: 0;
}

.kpi-icon-wrap.blue {
  background: #eff6ff;
  color: #2563eb;
}

.kpi-icon-wrap.amber {
  background: #fffbeb;
  color: #d97706;
}

.kpi-icon-wrap.green {
  background: #f0fdf4;
  color: #16a34a;
}

.kpi-icon-wrap.purple {
  background: #faf5ff;
  color: #7c3aed;
}

.kpi-meta {
  display: flex;
  flex-direction: column;
}

.kpi-label {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
}

.kpi-value {
  font-size: 1.5rem;
  font-weight: 800;
  color: #0f172a;
  margin: 2px 0;
}

.kpi-sub {
  font-size: 0.75rem;
  color: #94a3b8;
}

/* Controls Grid */
.sci-controls-grid {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 20px;
  margin-bottom: 24px;
}

@media (max-width: 992px) {
  .sci-controls-grid {
    grid-template-columns: 1fr;
  }
}

.sci-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.upload-inner {
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.upload-icon-box {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #f0f9ff;
  color: #0284c7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  flex-shrink: 0;
}

.upload-text-box h4 {
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.upload-text-box p {
  font-size: 0.8rem;
  color: #64748b;
  margin: 2px 0 0 0;
}

.upload-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  flex-wrap: wrap;
}

.sci-file-trigger {
  min-height: 42px;
  padding: 0 14px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sci-file-trigger:hover {
  border-color: #0284c7;
  color: #0284c7;
}

.sci-file-trigger input {
  display: none;
}

.filters-inner {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  height: 100%;
  box-sizing: border-box;
}

.date-filters {
  display: flex;
  align-items: center;
  gap: 12px;
}

.sci-input-field {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sci-input-field label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
}

.sci-input {
  min-height: 42px;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #0f172a;
  background: #f8fafc;
  outline: none;
  transition: all 0.2s;
  width: 100%;
}

.sci-input:focus {
  border-color: #0284c7;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.1);
}

.sci-search-field {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.sci-search-icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  font-size: 0.9rem;
}

.search-input {
  padding-left: 36px;
  padding-right: 32px;
}

.sci-clear-btn {
  position: absolute;
  right: 10px;
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
}

/* Table */
.sci-table-card {
  padding: 0;
  margin-bottom: 24px;
}

.table-responsive {
  overflow-x: auto;
}

.sci-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.sci-table th {
  background: #f8fafc;
  color: #475569;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
}

.sci-table td {
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  font-size: 0.95rem;
  vertical-align: middle;
}

.sci-table tbody tr:hover {
  background: #f8fafc;
}

.date-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: #334155;
}

.date-cell i {
  color: #94a3b8;
}

.partner-cell {
  display: flex;
  flex-direction: column;
}

.partner-name {
  font-weight: 700;
  color: #0f172a;
}

.partner-vat {
  font-size: 0.75rem;
  color: #64748b;
  font-family: monospace;
}

/* Status Pills */
.sci-pill-warning {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 50px;
  background: #fffbeb;
  color: #b45309;
  font-size: 0.85rem;
  font-weight: 700;
}

.sci-pill-info {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 50px;
  background: #e0f2fe;
  color: #0369a1;
  font-size: 0.85rem;
  font-weight: 700;
}

.sci-pill-success {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 50px;
  background: #dcfce7;
  color: #15803d;
  font-size: 0.85rem;
  font-weight: 700;
}

.sci-pill-neutral {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 50px;
  background: #f1f5f9;
  color: #475569;
  font-size: 0.85rem;
  font-weight: 600;
}

.table-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.sci-btn-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 38px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s;
}

.sci-btn-action.secondary {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #334155;
}

.sci-btn-action.secondary:hover {
  background: #e2e8f0;
}

.sci-btn-action.accent {
  background: #0284c7;
  color: #ffffff;
}

.sci-btn-action.accent:hover {
  background: #0369a1;
}

.sci-btn-action.danger {
  background: #fee2e2;
  color: #b91c1c;
}

.sci-btn-action.danger:hover {
  background: #ef4444;
  color: #ffffff;
}

.sci-btn-action.success {
  background: #dcfce7;
  color: #15803d;
}

.sci-btn-action.success:hover {
  background: #16a34a;
  color: #ffffff;
}

/* Modal Dialog */
.sci-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 20px;
  box-sizing: border-box;
}

.sci-modal-dialog {
  background: #f8fafc;
  border-radius: 20px;
  width: 95vw;
  max-width: 1400px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.sci-modal-header {
  padding: 20px 28px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-title-wrap h3 {
  font-size: 1.3rem;
  font-weight: 800;
  color: #0f172a;
  margin: 2px 0 0 0;
}

.sci-modal-close {
  background: transparent;
  border: none;
  font-size: 2rem;
  color: #64748b;
  cursor: pointer;
  line-height: 1;
}

.sci-modal-close:hover {
  color: #0f172a;
}

.sci-modal-body {
  padding: 24px;
  overflow-y: auto;
  display: grid;
  grid-template-columns: 420px 1fr;
  gap: 20px;
}

@media (max-width: 1100px) {
  .sci-modal-body {
    grid-template-columns: 1fr;
  }
}

.sci-card-inner {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.inner-header {
  margin-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 12px;
}

.inner-header h4 {
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.text-muted-sm {
  font-size: 0.8rem;
  color: #64748b;
  margin: 4px 0 0 0;
}

.form-row {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.flex-1 { flex: 1; }
.flex-2 { flex: 2; }

.form-group label {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  color: #475569;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.sci-file-viewer-box {
  background: #0f172a;
  border-radius: 10px;
  min-height: 380px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.img-preview {
  max-width: 100%;
  max-height: 480px;
  object-fit: contain;
}

.pdf-preview {
  width: 100%;
  height: 480px;
  border: none;
}

.sci-empty-doc {
  color: #94a3b8;
  text-align: center;
}

.sci-empty-doc i {
  font-size: 2.5rem;
  margin-bottom: 8px;
}

.modal-table-scroll {
  max-height: 480px;
  overflow-y: auto;
}

.sci-table.compact th, .sci-table.compact td {
  padding: 10px 12px;
  font-size: 0.85rem;
}

.sci-input-table {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.85rem;
  background: #f8fafc;
}

.sci-input-table:focus {
  border-color: #0284c7;
  background: #ffffff;
}

.code-badge-table {
  font-family: monospace;
  font-weight: 700;
  background: #f1f5f9;
  padding: 4px 6px;
  border-radius: 4px;
  color: #1e293b;
}

.sci-select-table {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.85rem;
  background: #f8fafc;
  outline: none;
}

.sci-select-table:focus {
  border-color: #0284c7;
}

/* Feedback States */
.sci-state-feedback {
  padding: 60px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.empty-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  background: #f1f5f9;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  margin-bottom: 16px;
}

.state-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px 0;
}

.state-subtitle {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0;
}
</style>
